import { useEffect, useId, useRef, useState } from "react";
import { styled } from "@pigment-css/react";
import { ChevronGrabberVertical, ChevronLeft, MicrophoneIcon } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { TopNav } from "@acto/taskpane/field-sales-2/components/patterns/TopNav";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { SummaryText } from "@acto/taskpane/field-sales-2/components/primitives/SummaryText";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import { PrimaryActionButton } from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapCreationFlow/ui/PrimaryActionButton";
import { VoiceBar } from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapCreationFlow/ui/VoiceBar";
import { useRecapTranscription } from "@acto/taskpane/field-sales-2/data-access/recap";
import { Collapse } from "@acto/taskpane/field-sales-2/components/primitives/Collapse";
import { CustomerPill } from "./CustomerPill";

const Page = styled("main")(({ theme }) => ({
  flex: 1,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  background: theme.fieldSalesTwo.colors.background,
}));

const Body = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  padding: `0 ${theme.fieldSalesTwo.spacing[20]} ${theme.fieldSalesTwo.spacing[24]}`,
}));

const ReadOnlySummary = styled(SummaryText)(({ theme }) => ({
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const Cards = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
}));

// Padding lives on the header/reveal, not the card, so a closed card collapses to the bare
// pill height with no dead inset.
const SummaryCard = styled("section")(({ theme }) => ({
  boxSizing: "border-box",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  borderRadius: theme.fieldSalesTwo.radius[38],
}));

const TranscriptCard = styled("section")(({ theme }) => ({
  boxSizing: "border-box",
  flex: 1,
  minHeight: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[10],
  background: "rgba(255, 255, 255, 0.32)",
  // Near-opaque rim from the design; no surface token matches (glassBorderStrong is 0.8).
  border: `${theme.strokeWidth.thin} solid rgba(255, 255, 255, 0.94)`,
  borderRadius: theme.fieldSalesTwo.radius[32],
  padding: theme.fieldSalesTwo.spacing[20],
}));

const CardHeaderButton = styled("button")<{ open: boolean }>(({ theme }) => ({
  appearance: "none",
  border: "none",
  background: "transparent",
  margin: 0,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.fieldSalesTwo.spacing[8],
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  color: theme.fieldSalesTwo.colors.neutral[700],
  padding: `${theme.fieldSalesTwo.spacing[14]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[14]} ${theme.fieldSalesTwo.spacing[20]}`,
  transition: `padding ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}`,
  ...pressInteraction(theme, { scale: 1 }),
  // After the spread: pressInteraction emits the same media key, so this merges its
  // transform reset with the padding transition reset instead of being overwritten.
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:active:not(:disabled)": { transform: "none" },
  },
  variants: [
    {
      props: { open: true },
      style: {
        padding: `${theme.fieldSalesTwo.spacing[20]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[20]}`,
      },
    },
  ],
}));

const CardLabel = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[16],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const Grabber = styled(ChevronGrabberVertical)(({ theme }) => ({
  flexShrink: 0,
  display: "block",
  width: theme.fieldSalesTwo.spacing[20],
  height: theme.fieldSalesTwo.spacing[20],
}));

// Held inside the Collapse so a closed section leaves no dead space above the transcript card.
const SummaryReveal = styled("div")(({ theme }) => ({
  padding: `0 ${theme.fieldSalesTwo.spacing[20]} ${theme.fieldSalesTwo.spacing[20]}`,
}));

const SummaryInput = styled("textarea")(({ theme }) => ({
  flex: 1,
  width: "100%",
  minHeight: "9rem",
  resize: "none",
  border: "none",
  background: "transparent",
  outline: "none",
  padding: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[16],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  "&::placeholder": { color: theme.fieldSalesTwo.colors.neutral[700] },
}));

const Footer = styled("div")(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[12],
  padding: `${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[32]}`,
  background: `linear-gradient(to top, ${theme.fieldSalesTwo.colors.background} 60%, rgba(240, 238, 237, 0))`,
}));

// Circular glass mic button (left of the save CTA).
const MicButton = styled("button")(({ theme }) => ({
  flexShrink: 0,
  width: theme.fieldSalesTwo.spacing[50],
  height: theme.fieldSalesTwo.spacing[50],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: theme.fieldSalesTwo.colors.surface.glassMedium,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  color: theme.fieldSalesTwo.colors.neutral[900],
  cursor: "pointer",
  "& svg": { width: theme.fieldSalesTwo.spacing[20], height: theme.fieldSalesTwo.spacing[20] },
  ...pressInteraction(theme, { scale: 0.96 }),
}));

export interface RecapEditSummaryScreenProps {
  customerName: string;
  customerReference: string;
  /** The AI-generated summary, shown read-only for reference. */
  aiSummary: string;
  /** The recap content to edit (the originally created recap). Also the baseline `canSave` compares against. */
  content: string;
  /** Opening draft when it differs from `content`, e.g. an edit whose save failed. */
  initialDraft?: string;
  /** Commit the edited recap content. */
  onSave: (content: string) => void;
  /** Leave the editor without saving. */
  onBack: () => void;
}

/**
 * Full-screen recap editor. Shows the AI summary in a collapsible card and lets the user edit the
 * recap content below it. Presentational: holds the content draft locally and emits it via `onSave`;
 * saving the content re-triggers AI regeneration (the caller's concern). Voice input appends to the
 * content draft, freezing the existing text and splicing the live session onto the end.
 */
export function RecapEditSummaryScreen({
  customerName,
  customerReference,
  aiSummary,
  content,
  initialDraft,
  onSave,
  onBack,
}: RecapEditSummaryScreenProps) {
  const t = useTranslations("fieldSalesTwo.recaps");
  const [draft, setDraft] = useState(initialDraft ?? content);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const summaryRegionId = useId();
  const summaryLabelId = useId();

  const transcription = useRecapTranscription();
  const frozenBaseRef = useRef("");

  // Splice live voice transcription onto the frozen base. Guarded on non-empty sessionText so the
  // mount-time run (sessionText === "") can't wipe the seeded `content`; only an active session writes.
  useEffect(() => {
    if (!transcription.sessionText) return;
    const base = frozenBaseRef.current;
    const separator = base.trim().length > 0 ? " " : "";
    setDraft(base + separator + transcription.sessionText);
  }, [transcription.sessionText]);

  const startRecording = () => {
    frozenBaseRef.current = draft;
    transcription.start();
  };

  const handleBack = () => {
    transcription.stop();
    onBack();
  };

  const canSave = !transcription.recording && draft.trim().length > 0 && draft.trim() !== content.trim();

  const transcriptInput = (
    <SummaryInput
      autoFocus
      value={draft}
      readOnly={transcription.recording}
      onChange={(e) => setDraft(e.target.value)}
      aria-label={t("edit.recapLabel")}
    />
  );

  return (
    <Page>
      <TopNav
        titlePlacement="leading"
        leading={
          <HeaderIconButton type="button" aria-label={t("a11y.back")} onClick={handleBack}>
            <ChevronLeft width={18} height={18} />
          </HeaderIconButton>
        }
        title={<CustomerPill name={customerName} reference={customerReference} align="start" />}
      />

      <Body>
        <Cards>
          {aiSummary.trim().length > 0 && (
            <SummaryCard>
              <CardHeaderButton
                type="button"
                open={summaryOpen}
                aria-expanded={summaryOpen}
                aria-controls={summaryRegionId}
                onClick={() => setSummaryOpen((open) => !open)}
              >
                <CardLabel id={summaryLabelId}>{t("edit.aiSummaryLabel")}</CardLabel>
                <Grabber />
              </CardHeaderButton>
              <Collapse open={summaryOpen} id={summaryRegionId} role="region" aria-labelledby={summaryLabelId}>
                <SummaryReveal>
                  <ReadOnlySummary>{aiSummary}</ReadOnlySummary>
                </SummaryReveal>
              </Collapse>
            </SummaryCard>
          )}

          <TranscriptCard>
            <CardLabel>{t("edit.recapLabel")}</CardLabel>
            {transcriptInput}
          </TranscriptCard>
        </Cards>
      </Body>

      <Footer>
        {transcription.recording ? (
          <VoiceBar elapsedSeconds={transcription.elapsedSeconds} onStop={transcription.stop} />
        ) : (
          <>
            <MicButton type="button" aria-label={t("a11y.startRecording")} onClick={startRecording}>
              <MicrophoneIcon />
            </MicButton>
            <PrimaryActionButton
              fullWidth={false}
              type="button"
              disabled={!canSave}
              onClick={() => onSave(draft.trim())}
            >
              {t("edit.save")}
            </PrimaryActionButton>
          </>
        )}
      </Footer>
    </Page>
  );
}
